import { ThemeProvider, ToastHost, TruncationTooltipHost } from "@carbon6/c6-frontend-design";
import { BrowserRouter } from "react-router";
import { getReservedKeyContracts, getReviewOptions, listAllLabels } from "@/api";
import { AppSessionLifecycle, AppStateProvider, useThemeModePreference } from "@/app";
import { AppSplash } from "@/components/base";
import { AppRouter } from "@/page/router";
import { windowStack } from "@/app/windowStack";
import { AppWindowHost } from "@/page/router/AppWindowHost";
import { uploadTabTitle } from "@/page/upload/uploadTabTitle";
import { uploadEngine } from "@/upload";
import { serverInfoController } from "@/app/serverInfoController";
import { endHatcheryDatabaseSession, startHatcheryDatabaseSession } from "@/app/hatcheryDatabase";

const appSessionLifecycle = new AppSessionLifecycle([
  {
    // 브라우저 캐시는 다른 자원보다 먼저 열고 나중에 닫는다. 정리는 역순이므로, 이 위치에 두면
    // 캐시는 쓰는 쪽이 모두 멈춘 뒤에 닫힌다.
    name: "hatcheryDatabase",
    start: (session) => startHatcheryDatabaseSession(session),
    dispose: (reason) => endHatcheryDatabaseSession(reason),
  },
  {
    name: "serverInfo",
    start: () => serverInfoController.init(),
    dispose: () => serverInfoController.dispose(),
  },
  {
    name: "uploadEngine",
    start: () => uploadEngine.startSession(),
    dispose: () => uploadEngine.disposeSession(),
  },
  {
    // 단절로 일시정지한 업로드를 탭 제목으로 알린다. 엔진 뒤에 둔다. 정리는 역순이므로 엔진보다
    // 먼저 구독을 끊는다.
    name: "uploadTabTitle",
    start: () => uploadTabTitle.start(),
    dispose: () => uploadTabTitle.dispose(),
  },
  {
    name: "windowStack",
    start: () => undefined,
    dispose: () => windowStack.clear(),
  },
  {
    // 라벨 입력에 필요한 세 가지를 세션이 열릴 때 미리 받는다.
    //
    // 라벨 칩 렌더와 facet 구성은 reserved 계약(allowlist·필수 여부)을 **동기로** 읽는다. 라벨
    // 카탈로그 표는 리뷰 어휘를 **동기로** 읽는다. 캐시가 비어 있으면 첫 렌더가 fallback vocab 을 표시한다. 라벨 카탈로그를 동기로
    // 읽는 소비처는 없다. 하지만 lazy 로 두면 첫 소비처(새 라벨 창·작업 축 입력·필터 시트)를 열 때
    // 전체 로드가 시작된다. 로드하는 동안 화면은 "기존 라벨이 없다" 로 표시된다.
    //
    // 정리는 여기서 하지 않는다. 세션 경계에서 `resetSessionCatalogs()` 가 세 캐시를 모두 비운다.
    name: "labelInputWarmup",
    start: () => {
      void getReservedKeyContracts();
      void getReviewOptions();
      void listAllLabels();
    },
    dispose: () => undefined,
  },
]);

export function AppRoot() {
  const [themeMode] = useThemeModePreference();

  return (
    <ThemeProvider mode={themeMode}>
      <AppStateProvider splash={<AppSplash />} sessionLifecycle={appSessionLifecycle}>
        {/* BrowserRouter 를 앱 셸 최상위에 둔다. 그러면 라우트와 전역 오버레이(예: 모달 호스트)가
            모두 라우터 컨텍스트(useNavigate)를 쓸 수 있다. */}
        <BrowserRouter>
          <AppRouter />
          {/* 라우트 밖에 둔다. 그러면 최소화한 전체화면 창(업로드·데이터셋 상세·리뷰 등)이 화면
              전환 뒤에도 유지되고 복원된다. 진행 중인 업로드도 이 창(작업표시줄)으로 유지된다. */}
          <AppWindowHost />
          {/* 라우트 밖에 둔다. 그러면 화면을 옮겨도 일시 알림(토스트)이 유지되고 표시된다. */}
          <ToastHost gravity="bottom-center" variant="icon-stripe" />
          {/* 잘린 텍스트의 전체 값을 표시하는 공유 버블이다. 페이지마다 두면 호스트가 없는 화면의
              잘림 마커가 경고 없이 동작하지 않는다. 그래서 앱 루트에 한 번만 둔다. */}
          <TruncationTooltipHost placement="top" />
        </BrowserRouter>
      </AppStateProvider>
    </ThemeProvider>
  );
}
